import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  ApartmentOutlined,
  DisconnectOutlined,
  DollarOutlined,
  PlusOutlined,
  ReloadOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { BRANCH_USER_ROLE_OPTIONS, branchNoticeMessage } from './branchUsers';
import MainFacilitySelect from './MainFacilitySelect';
import FacilityGroupEncounters from './FacilityGroupEncounters';

const { Text, Paragraph } = Typography;

const TARIFF_STATUS_META = {
  linked: { color: 'blue', label: 'Using main tariff' },
  own: { color: 'green', label: 'Own tariff' },
  standard: { color: 'purple', label: 'HMO standard' },
  none: { color: 'default', label: 'No tariff' },
};

/**
 * "Branches" tab on a facility's admin detail page.
 *
 * Shows the group this facility belongs to and lets an admin attach an existing
 * facility as a branch, detach one, or put a branch on the main's tariff.
 * Onboarding a brand-new facility as a branch happens in the onboarding modal
 * (it also provisions portal credentials), so this tab only attaches facilities
 * that already exist.
 *
 * When the facility being viewed is itself a branch, the tab becomes read-only
 * and points at the main — branches cannot have branches.
 */
export default function FacilityBranchesTab({ facilityId, facilityName, canManage, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);

  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachTariffMode, setAttachTariffMode] = useState('linked');
  const [attaching, setAttaching] = useState(false);

  const [tariffTarget, setTariffTarget] = useState(null);
  const [tariffMode, setTariffMode] = useState('linked');
  const [assigning, setAssigning] = useState(false);

  const [createUserTarget, setCreateUserTarget] = useState(null);
  const [createUserSaving, setCreateUserSaving] = useState(false);
  const [createUserForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = lib.parseApiEnvelope(await lib.getFacilityBranchesV2(facilityId));
      if (!parsed.ok) {
        setError(parsed.message || 'Failed to load branches');
        setGroup(null);
        return;
      }
      setGroup(parsed.data || null);
    } catch (e) {
      setError(e?.message || 'Failed to load branches');
      setGroup(null);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  const isBranch = !!group?.is_branch;
  const mainId = group?.main_facility_id ?? facilityId;
  const branches = useMemo(() => group?.branches || [], [group]);
  const branchingDisabled = group?.enabled === false;

  const attach = async () => {
    if (!attachTarget) {
      notification.warning({ message: 'Select a facility to attach', placement: 'topLeft' });
      return;
    }
    setAttaching(true);
    try {
      // The API adopts the main's tariff (every category it has active) by default —
      // "none" is an explicit opt-out, not just an omission, so it must be sent.
      const payload = { branch_facility_id: attachTarget };
      if (attachTariffMode === 'none') {
        payload.skip_tariff_assignment = true;
      } else {
        payload.tariff_mode = attachTariffMode;
      }
      const parsed = lib.parseApiEnvelope(await lib.attachFacilityBranchV2(mainId, payload));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to attach branch', placement: 'topLeft' });
        return;
      }
      const skipped = parsed.body?.data?.branch?.tariff_assignment?.skipped || [];
      notification.success({
        message: parsed.message || 'Branch attached',
        description: skipped.length
          ? `Tariff not assigned for ${skipped.length} categor${skipped.length === 1 ? 'y' : 'ies'} — the main has nothing active there yet.`
          : undefined,
        placement: 'topLeft',
      });
      setAttachOpen(false);
      setAttachTarget(null);
      setAttachTariffMode('linked');
      await load();
      onChanged?.();
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to attach branch', placement: 'topLeft' });
    } finally {
      setAttaching(false);
    }
  };

  const detach = async (branchId) => {
    try {
      const parsed = lib.parseApiEnvelope(await lib.detachFacilityBranchV2(mainId, branchId));
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to detach branch', placement: 'topLeft' });
        return;
      }
      notification.success({ message: parsed.message || 'Branch detached', placement: 'topLeft' });
      await load();
      onChanged?.();
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to detach branch', placement: 'topLeft' });
    }
  };

  const assignTariff = async () => {
    if (!tariffTarget) return;
    setAssigning(true);
    try {
      const parsed = lib.parseApiEnvelope(
        await lib.assignBranchTariffV2(mainId, tariffTarget.facility_id, { mode: tariffMode }),
      );
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Failed to assign tariff', placement: 'topLeft' });
        return;
      }
      notification.success({ message: parsed.message || 'Tariff assigned', placement: 'topLeft' });
      setTariffTarget(null);
      await load();
    } catch (e) {
      notification.error({ message: e?.message || 'Failed to assign tariff', placement: 'topLeft' });
    } finally {
      setAssigning(false);
    }
  };

  const openCreateUserModal = (branch) => {
    createUserForm.resetFields();
    setCreateUserTarget(branch);
  };

  const closeCreateUserModal = () => {
    setCreateUserTarget(null);
  };

  const submitCreateUser = async (values) => {
    if (!createUserTarget) return;
    setCreateUserSaving(true);
    try {
      const resp = await lib.createFacilityUser(createUserTarget.facility_id, {
        full_name: String(values.full_name || '').trim(),
        email: String(values.email || '').trim().toLowerCase(),
        phone: String(values.phone || '').trim(),
        facility_role_id: values.facility_role_id,
        password: values.password,
      });
      const parsed = lib.parseApiEnvelope(resp);
      if (!parsed.ok) {
        notification.error({ message: parsed.message || 'Unable to create facility user', placement: 'topLeft' });
        return;
      }
      closeCreateUserModal();
      const notice = branchNoticeMessage(parsed.data?.branch_notification || parsed.body?.data?.branch_notification);

      // The login code is shown in a modal that has to be dismissed, not a toast
      // that fades. If this user has no email address there is nowhere else it
      // exists — the person who just created them has to be able to write it down.
      const username =
        parsed.data?.username || parsed.data?.facility_users_username || parsed.body?.username || null;

      if (username) {
        Modal.success({
          title: 'Facility user created',
          okText: 'I have noted the code',
          content: (
            <div>
              <Paragraph style={{ marginBottom: 8 }}>
                <Text strong>{String(values.full_name || '').trim()}</Text> signs in with this code:
              </Paragraph>
              <Paragraph copyable={{ text: username }} style={{ marginBottom: 8 }}>
                <Text code style={{ fontSize: 20, letterSpacing: 1 }}>{username}</Text>
              </Paragraph>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Give it to them along with the password you set. It is also listed
                against their name in the facility&apos;s Users tab if they forget it.
              </Paragraph>
              {notice ? (
                <Alert style={{ marginTop: 12 }} type={notice.type} showIcon message={notice.text} />
              ) : null}
            </div>
          ),
        });
      } else {
        notification.success({ message: parsed.message || 'Facility user created', description: notice?.text, placement: 'topLeft' });
      }
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Unable to create facility user',
        placement: 'topLeft',
      });
    } finally {
      setCreateUserSaving(false);
    }
  };

  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Branch',
        dataIndex: 'facility_name',
        key: 'facility_name',
        render: (value, row) => (
          <div>
            <Text strong>{value || '—'}</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[row.city, row.state].filter(Boolean).join(', ') || '—'}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: 'Code',
        dataIndex: 'facility_code',
        key: 'facility_code',
        render: (value) => value || '—',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (value) =>
          Number(value) === 1
            ? <Tag color="green">Active</Tag>
            : <Tag color="red">Inactive</Tag>,
      },
      {
        title: 'Tariff',
        dataIndex: 'tariff',
        key: 'tariff',
        render: (tariff) => {
          const meta = TARIFF_STATUS_META[tariff?.status] || TARIFF_STATUS_META.none;
          return (
            <Tooltip title={tariff?.tariff_name || undefined}>
              <Tag color={meta.color}>{meta.label}</Tag>
            </Tooltip>
          );
        },
      },
    ];

    if (canManage) {
      cols.push({
        title: 'Actions',
        key: 'actions',
        render: (_, row) => (
          <Space>
            <Button
              size="small"
              icon={<UserAddOutlined />}
              onClick={() => openCreateUserModal(row)}
            >
              User
            </Button>
            <Button
              size="small"
              icon={<DollarOutlined />}
              onClick={() => {
                setTariffTarget(row);
                setTariffMode('linked');
              }}
            >
              Tariff
            </Button>
            <Popconfirm
              title="Detach this branch?"
              description="It becomes a standalone facility. Its encounters, users and tariff stay with it."
              okText="Detach"
              okButtonProps={{ danger: true }}
              onConfirm={() => detach(row.facility_id)}
            >
              <Button size="small" danger icon={<DisconnectOutlined />}>
                Detach
              </Button>
            </Popconfirm>
          </Space>
        ),
      });
    }

    return cols;
    // detach is stable enough for this table; re-created only when the group id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, mainId]);

  if (loading && !group) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (branchingDisabled) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Facility branching is not enabled on this environment"
        description="Run sqls/migrations/20260826_facility_branching.sql to enable it."
      />
    );
  }

  return (
    <div>
      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      {isBranch ? (
        <Alert
          type="info"
          showIcon
          icon={<ApartmentOutlined />}
          style={{ marginBottom: 16 }}
          message={`${facilityName || 'This facility'} is a branch of ${group?.main_facility?.facility_name || `facility #${mainId}`}`}
          description="Branches cannot have branches of their own. Manage this group from the main facility."
        />
      ) : (
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space size="large">
            <Statistic title="Branches" value={branches.length} prefix={<ApartmentOutlined />} />
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
            {canManage ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAttachOpen(true)}>
                Add branch
              </Button>
            ) : null}
          </Space>
        </Space>
      )}

      {branches.length === 0 ? (
        <Empty
          description={
            isBranch
              ? 'This main facility has no other branches yet'
              : 'No branches yet — attach an existing facility, or onboard a new one as a branch'
          }
        />
      ) : (
        <Table
          rowKey="facility_id"
          size="small"
          dataSource={branches}
          columns={columns}
          pagination={false}
        />
      )}

      <div style={{ marginTop: 32 }}>
        <FacilityGroupEncounters facilityId={mainId} />
      </div>

      <Modal
        title="Attach a branch facility"
        open={attachOpen}
        onCancel={() => setAttachOpen(false)}
        onOk={attach}
        confirmLoading={attaching}
        okText="Attach"
      >
        <Paragraph type="secondary">
          Pick a facility that already exists. It becomes a branch of{' '}
          <Text strong>{facilityName || `facility #${mainId}`}</Text>: encounter codes
          become interchangeable between them, and its encounters roll up here.
        </Paragraph>
        <label className="facility-form-label">Facility</label>
        <MainFacilitySelect
          value={attachTarget}
          onChange={setAttachTarget}
          excludeFacilityId={mainId}
          // Any standalone facility can become a branch, so do not restrict to mains.
          mainsOnly={false}
          placeholder="Search facilities to attach…"
          size="middle"
        />
        <div style={{ marginTop: 16 }}>
          <label className="facility-form-label">Tariff</label>
          <Radio.Group
            value={attachTariffMode}
            onChange={(e) => setAttachTariffMode(e.target.value)}
          >
            <Space direction="vertical" size={2}>
              <Radio value="none">Leave the branch on its own tariff</Radio>
              <Radio value="linked">Use the main facility&apos;s tariff (tracks future updates)</Radio>
              <Radio value="copy">Copy the main facility&apos;s tariff (branch can edit it)</Radio>
            </Space>
          </Radio.Group>
        </div>
      </Modal>

      <Modal
        title={`Assign tariff — ${tariffTarget?.facility_name || ''}`}
        open={!!tariffTarget}
        onCancel={() => setTariffTarget(null)}
        onOk={assignTariff}
        confirmLoading={assigning}
        okText="Assign"
      >
        <Paragraph type="secondary">
          Put this branch on <Text strong>{facilityName || 'the main facility'}</Text>&apos;s
          active tariff.
        </Paragraph>
        <Radio.Group value={tariffMode} onChange={(e) => setTariffMode(e.target.value)}>
          <Space direction="vertical">
            <Radio value="linked">
              <Text strong>Linked</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  The branch points at the main facility&apos;s tariff. When the main
                  facility&apos;s tariff changes, the branch follows automatically.
                </Text>
              </div>
            </Radio>
            <Radio value="copy">
              <Text strong>Copy</Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Take a snapshot into a branch-owned tariff. It can then be edited
                  independently and no longer tracks the main facility.
                </Text>
              </div>
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>

      <Modal
        title={`Add user — ${createUserTarget?.facility_name || ''}`}
        open={!!createUserTarget}
        onCancel={closeCreateUserModal}
        onOk={() => createUserForm.submit()}
        confirmLoading={createUserSaving}
        okText="Create user"
        destroyOnClose
      >
        <Paragraph type="secondary">
          This creates a facility_users account scoped to this branch — a branch has no
          portal login of its own, so this is the only way in for its staff.
        </Paragraph>
        <Form form={createUserForm} layout="vertical" onFinish={submitCreateUser}>
          <Form.Item
            label="Full name"
            name="full_name"
            rules={[{ required: true, message: 'Full name is required' }]}
          >
            <Input placeholder="Jane Doe" allowClear />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            // Optional: staff sign in with the generated login code, so an address
            // is contact detail rather than identity. Many ward staff have none,
            // and requiring one led to a manager's address being borrowed.
            extra="Optional. The user signs in with the login code shown after you save."
            rules={[{ type: 'email', message: 'Enter a valid email address' }]}
          >
            <Input placeholder="name@example.com (optional)" autoComplete="email" allowClear />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={[{ required: true, message: 'Phone is required' }]}
          >
            <Input placeholder="+234 …" allowClear />
          </Form.Item>

          <Form.Item
            label="Role"
            name="facility_role_id"
            rules={[{ required: true, message: 'Select a role' }]}
          >
            <Select
              placeholder="Select a role"
              options={BRANCH_USER_ROLE_OPTIONS}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="At least 8 characters" />
          </Form.Item>

          <Form.Item
            label="Confirm password"
            name="confirm_password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
